import openModal from 'App/services/Modal';
import { openDrawer } from 'Services/Drawer';
import { openEditOrder } from 'App/components/EditOrder';
import { openEditAppointment } from 'Components/Modals/Appointment/EditAppointment';
import { openEditContact } from 'App/components/EditContact';
import logError from 'App/babel/helpers/logError';

/**
 * Opening a proposal in the real create form.
 *
 * `openCrmEntity` routes to the same surfaces but only for a record that
 * already exists — it takes an id and returns void. This is the other half:
 * hand an UNSAVED proposal payload to the entity's real create modal/drawer so
 * the user gets every field the card cannot show (an appointment's agenda,
 * custom fields, invited-contact toggles…), then report back what was saved.
 *
 * Two things make this more than a switch statement:
 *
 * 1. **The surfaces disagree on how they hand back a saved record.** The modal
 *    adapters (`openEditAppointment`, `openEditOrder`, `openEditContact`)
 *    return a promise; the drawers take an `onSave` prop and return nothing;
 *    `openModal` takes an `onClose`. All three are normalised to one promise
 *    here so the caller has a single shape to resolve an approval against.
 * 2. **The payload is the agent's shape, not the form's.** A proposal carries
 *    `users: [{id}]` / `contacts: [{id}]` because that is what the v2 API takes;
 *    `CreateTodo` and `CreateCall` want a singular `user` and `contact`. The
 *    per-entity mappers below are that translation and nothing more.
 *
 * Resolves with the saved record, or `null` when the user closed the form
 * without saving — the caller must leave the proposal pending in that case.
 */

type Ref = { id: number; name?: string } | null;

type SavedRecord = { id: number } & Record<string, unknown>;

/** A `{id, name}` ref off the payload, accepting the bare number the
 *  multi-action card sends. Returns null when there is nothing usable. */
function toRef(raw: unknown): Ref {
	if (typeof raw === 'number' && Number.isFinite(raw)) return { id: raw };
	if (typeof raw === 'string' && raw !== '' && Number.isFinite(Number(raw))) return { id: Number(raw) };
	if (raw && typeof raw === 'object') {
		const o = raw as { id?: unknown; name?: unknown };
		const id = typeof o.id === 'number' ? o.id : typeof o.id === 'string' ? Number(o.id) : NaN;
		if (Number.isFinite(id)) {
			return typeof o.name === 'string' ? { id, name: o.name } : { id };
		}
	}
	return null;
}

/** The first ref of a plural field. The agent sends `users` / `contacts` as
 *  arrays even when the form only takes one. */
function firstRef(raw: unknown): Ref {
	if (Array.isArray(raw)) {
		for (const item of raw) {
			const ref = toRef(item);
			if (ref) return ref;
		}
		return null;
	}
	return toRef(raw);
}

function str(raw: unknown): string | undefined {
	return typeof raw === 'string' && raw ? raw : undefined;
}

/**
 * A real `Date` for the forms that do date maths on the value.
 *
 * The agent sends ISO strings because that is what the v2 API takes, but
 * `Appointment.new()` seeds `date` / `endDate` as `Date` objects and the
 * modal's calendar calls `.getTime()` on whatever it finds there. Merging a
 * string over that seed threw `event.date.getTime is not a function` and the
 * modal never rendered. Left undefined when the value is missing or unparseable
 * so the form keeps its own default rather than showing an Invalid Date.
 */
function toDate(raw: unknown): Date | undefined {
	if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? undefined : raw;
	if (typeof raw !== 'string' && typeof raw !== 'number') return undefined;
	const parsed = new Date(raw);
	return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/** A ref shaped for a form prop that wants `{id, name}` with a real name —
 *  several forms render the name straight into a select without fetching. */
function namedRef(raw: unknown): { id: number; name: string } | undefined {
	const ref = toRef(raw);
	return ref ? { id: ref.id, name: ref.name ?? '' } : undefined;
}

/** Wraps a surface that reports its result through a callback prop. */
function viaCallback(open: (report: (saved?: unknown) => void) => void): Promise<SavedRecord | null> {
	return new Promise(resolve => {
		let reported = false;
		open(saved => {
			// A modal's close fires on cancel too, with nothing — and can fire
			// twice (save, then unmount). First call wins; no id means no save.
			if (reported) return;
			reported = true;
			const record = saved && typeof saved === 'object' ? (saved as SavedRecord) : null;
			resolve(record && typeof record.id === 'number' ? record : null);
		});
	});
}

/** Wraps a surface that returns a promise. A rejection here is the user
 *  dismissing the form (the adapters reject on empty), not a failure. */
async function viaPromise(open: () => Promise<unknown>): Promise<SavedRecord | null> {
	try {
		const saved = await open();
		if (saved && typeof saved === 'object' && typeof (saved as SavedRecord).id === 'number') {
			return saved as SavedRecord;
		}
		return null;
	} catch (err) {
		// Dismissal and a real failure are indistinguishable at this boundary,
		// so log rather than swallow and treat both as "nothing was saved".
		logError(err, '[openCrmEntityDraft] form closed without a saved record');
		return null;
	}
}

type Opener = (data: Record<string, unknown>) => Promise<SavedRecord | null>;

const OPENERS: Record<string, Opener> = {
	appointment: data =>
		viaPromise(() =>
			openEditAppointment({
				// The appointment payload already matches the model, so this is a
				// pass-through: useMeta merges it onto Appointment.new() and
				// hydrates the client and contacts from their ids.
				appointment: {
					description: str(data.description),
					date: toDate(data.date),
					endDate: toDate(data.endDate),
					notes: str(data.notes),
					location: str(data.location),
					client: namedRef(data.client),
					activityType: namedRef(data.activityType),
					users: Array.isArray(data.users) ? data.users.map(toRef).filter(Boolean) : undefined,
					contacts: Array.isArray(data.contacts) ? data.contacts.map(toRef).filter(Boolean) : undefined
				} as any
			})
		),

	order: data => viaPromise(() => openEditOrder({ order: data as any })),

	client: data =>
		viaCallback(report =>
			openModal('EditClient', {
				prefillData: data as any,
				noRedirect: true,
				onClose: report as any
			})
		),

	contact: data =>
		viaPromise(() =>
			openEditContact({
				contact: data as any,
				clientId: toRef(data.client)?.id
			})
		),

	// The activity family shares one payload shape but three different forms.
	activity: data =>
		viaCallback(report =>
			openModal('EditActivity', {
				activity: {
					description: str(data.description),
					date: toDate(data.date),
					notes: str(data.notes),
					client: namedRef(data.client),
					activityType: namedRef(data.activityType),
					users: Array.isArray(data.users) ? data.users.map(toRef).filter(Boolean) : undefined,
					contacts: Array.isArray(data.contacts) ? data.contacts.map(toRef).filter(Boolean) : undefined
				} as any,
				onClose: report as any
			})
		),

	todo: data =>
		viaCallback(report =>
			openDrawer('CreateTodo', {
				description: str(data.description),
				date: data.date as any,
				notes: str(data.notes),
				client: namedRef(data.client) as any,
				contact: firstRef(data.contacts) as any,
				user: firstRef(data.users)?.id,
				onSave: report as any
			})
		),

	phonecall: data =>
		viaCallback(report =>
			openDrawer('CreateCall', {
				initialDescription: str(data.description),
				date: data.date as any,
				notes: str(data.notes),
				client: namedRef(data.client) as any,
				contact: firstRef(data.contacts) as any,
				onSave: report as any
			})
		)
};

/** Entity aliases → the opener that knows their form. Mirrors the alias sets in
 *  `openCrmEntity` so both surfaces answer for the same vocabulary. */
const ALIASES: Record<string, keyof typeof OPENERS> = {
	appointment: 'appointment',
	meeting: 'appointment',
	order: 'order',
	deal: 'order',
	opportunity: 'order',
	client: 'client',
	account: 'client',
	company: 'client',
	contact: 'contact',
	person: 'contact',
	activity: 'activity',
	task: 'activity',
	todo: 'todo',
	phonecall: 'phonecall'
};

/**
 * True when this entity has a real create form we can prefill.
 *
 * Deliberately smaller than `canOpenCrmEntity`: that one answers "can I show an
 * existing record", which is true for surfaces that have no create form worth
 * opening (a segment, a campaign dashboard) or that the agent cannot create at
 * all (lead, agreement — both blocked in `crmEntityMap`). A card whose entity
 * is missing here simply gets no Edit action.
 */
export function canEditCrmEntityDraft(entity: string | undefined): boolean {
	return !!entity && ALIASES[entity.toLowerCase()] !== undefined;
}

export function openCrmEntityDraft(
	entity: string,
	data: Record<string, unknown>
): Promise<SavedRecord | null> {
	const key = ALIASES[entity.toLowerCase()];
	if (!key) return Promise.resolve(null);
	return OPENERS[key](data);
}
